// SEO utility functions and constants

// Convert date string to ISO 8601 format with timezone
// Accepts dates in formats like "2025-03-01" or "2025-03-01T12:00:00Z"
export const toISODateTime = (dateString: string | undefined): string => {
  if (!dateString) {
    return new Date().toISOString();
  }

  // If already in ISO format with timezone, return as is
  if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+') || dateString.match(/-\d{2}:\d{2}$/))) {
    return dateString;
  }

  // If just a date (YYYY-MM-DD), add time as midnight UTC
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return `${dateString}T00:00:00Z`;
  }

  // Try to parse and convert to ISO
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch (e) {
    // Fall back to current date if parsing fails
  }

  return new Date().toISOString();
};

// Format a date for HUMAN-VISIBLE display, deterministically across environments.
//
// The site is statically prerendered on the build server (which runs in UTC) and
// then hydrated in the visitor's local timezone (the browser). Formatting a date
// with `new Date(iso).toLocaleDateString(...)` WITHOUT a fixed `timeZone` therefore
// renders a different calendar day on the server than on the client for any
// timestamp near a day boundary — which React reports as a hydration mismatch
// (#418 / #423 / #425). Pinning BOTH the locale ('en-US') and the timeZone ('UTC')
// makes the output byte-identical everywhere, so hydration always matches.
//
// Use this for every date TEXT shown on a prerendered page. Do NOT use it for
// machine-readable metadata (JSON-LD, <meta>, datetime attributes) — those must
// stay ISO 8601 via toISODateTime.
export const formatDisplayDate = (
  input: string | number | Date | null | undefined
): string => {
  if (input === null || input === undefined || input === '') return '';
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

export const generateArticleStructuredData = (article: any) => {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.technical-leaders.com/post/${article.slug}`
    },
    "headline": article.title,
    "description": article.description,
    "image": article.featuredImage || "https://www.technical-leaders.com/opengraph-image-p98pqg.png",
    "author": {
      "@type": "Person",
      "name": article.author?.name || "Technical Leaders",
      "jobTitle": article.author?.role || "Technical Expert"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Technical Leaders",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.technical-leaders.com/favicon.webp"
      }
    },
    "datePublished": toISODateTime(article.publishedAt),
    "dateModified": toISODateTime(article.updatedAt || article.publishedAt),
    "keywords": article.tags?.join(", ") || ""
  };
};

export const generateCourseStructuredData = (courseName: string, description: string, price?: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": courseName,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": "Technical Leaders",
      "sameAs": "https://www.technical-leaders.com"
    },
    "offers": price ? {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    } : undefined,
    "courseMode": "online",
    "educationalLevel": "advanced"
  };
};

export const generateBreadcrumbStructuredData = (items: Array<{name: string, url: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

export const generateFAQStructuredData = (faqs: Array<{question: string, answer: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Technical Leaders",
  "alternateName": "Tech Leaders",
  "url": "https://www.technical-leaders.com",
  "logo": "https://www.technical-leaders.com/favicon.webp",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@technical-leaders.com"
  },
  "sameAs": [
    "https://twitter.com/technical_leaders",
    "https://www.linkedin.com/company/technical-leaders"
  ],
  "description": "Technical Leaders is a community and platform for CTOs, VPs of Engineering, and technical executives to advance their careers through coaching, programs, and peer connections."
};

// Common meta descriptions for different page types
export const metaDescriptions = {
  home: "Transform your technical expertise into strategic leadership. Join CTOs, VPs of Engineering, and technical executives advancing their careers and organizations.",
  launch: "6-week intensive program for senior engineers ready to become CTOs, VPs, and technical executives. Build authority, expand income, and lead at scale.",
  scale: "Transform your expertise into a scalable business. For technical leaders ready to build consulting practices, advisory roles, and thought leadership platforms.",
  articles: "Practical insights, strategies, and tools for CTOs, VPs of Engineering, and technical executives. Learn from real-world experiences in scaling teams and advancing careers.",
  call: "Schedule a free strategy call to discuss how to advance your technical leadership career. Get personalized guidance from experienced CTOs and tech executives."
};

// SEO-friendly URL slugs
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Generate meta keywords from content
export const generateKeywords = (content: string, additionalKeywords: string[] = []): string[] => {
  const baseKeywords = ['technical leadership', 'CTO', 'VP Engineering', 'tech executive'];
  
  // Extract important words from content (this is a simple implementation)
  const contentWords = content
    .toLowerCase()
    .split(/\s+/)
    .filter(word => word.length > 5)
    .slice(0, 5);
  
  return [...new Set([...baseKeywords, ...additionalKeywords, ...contentWords])];
};
